import React from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import ComponentDemo from '../../components/ComponentDemo';
import CodeBlock from '../../components/CodeBlock';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

const WarningBox = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(239, 68, 68, 0.1)'
      : 'rgba(239, 68, 68, 0.05)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(239, 68, 68, 0.3)'
        : 'rgba(239, 68, 68, 0.2)'};
  border-left: 4px solid #ef4444;
  border-radius: 8px;
  padding: 16px;
  margin: 24px 0;
`;

const WarningTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ef4444;
  margin-bottom: 8px;
`;

const WarningText = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.dark
      : props.theme.colors.textGray.secondary};
`;

const RoutingDocs = () => {
  return (
    <div>
      <PageHeader>
        <Title>Context Routing Pattern</Title>
        <Subtitle>
          This application has three distinct admin contexts (Track Admin, Employee
          Admin, and Regular Admin) that share components. Components must be
          context-aware when creating navigation links.
        </Subtitle>
      </PageHeader>

      <WarningBox>
        {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
        <WarningTitle>⚠️ Critical: Context-Aware Navigation</WarningTitle>
        <WarningText>
          All components that create links MUST detect their current context and use the
          appropriate route prefix. Failure to do this will cause navigation to break
          when components are used across different admin contexts.
        </WarningText>
      </WarningBox>

      <Section>
        <SectionTitle>Route Structure</SectionTitle>
        <SectionDescription>
          The application has three distinct admin contexts with different route
          prefixes:
        </SectionDescription>

        <Card>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            • <strong>Track Admin</strong>: Routes prefixed with <code>/admin-track/</code>
            <br />
            • <strong>Employee Admin</strong>: Routes prefixed with{' '}
            <code>/admin-employee/</code>
            <br />
            • <strong>Regular Admin</strong>: Routes prefixed with <code>/admin/</code>
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>The Pattern</SectionTitle>
        <SectionDescription>
          When a component can be used in multiple admin contexts, it MUST detect the
          current route context and use the appropriate prefix for all navigation links.
        </SectionDescription>

        <CodeBlock>
{`import { useLocation } from 'react-router-dom';

const Component = () => {
  const location = useLocation();
  const { pathname } = location;

  // Determine current context
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin ? '/admin-track' :
                      isEmployeeAdmin ? '/admin-employee' : '/admin';

  // Use dynamic prefix in navigation
  return <Link to={\`\${routePrefix}/users/transactions/\${id}\`}>View</Link>;
};`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Real-World Examples</SectionTitle>
        <SectionDescription>
          Here are examples from actual components in the codebase that implement
          context-aware routing correctly.
        </SectionDescription>

        <Card title="CustomerTable Component" headerBorder>
          <CodeBlock>
{`import { Link, useLocation } from 'react-router-dom';
import TableActionButton from 'components/TableActionButton';

const CustomerTable = ({ accountId, customers }) => {
  const location = useLocation();
  const { pathname } = location;

  // Detect current context
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin ? '/admin-track' :
                      isEmployeeAdmin ? '/admin-employee' : '/admin';

  return (
    <StyledTable>
      <TableBody>
        {customers.map((customer) => (
          <TableRow key={customer.id}>
            <TableCell>{customer.name}</TableCell>
            <TableCell>
              {/* Context-aware navigation */}
              <Link to={\`\${routePrefix}/customers/\${customer.id}\`}>
                <TableActionButton icon="fa-eye" />
              </Link>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </StyledTable>
  );
};`}
          </CodeBlock>
        </Card>

        <Card title="QuickActions Component" headerBorder style={{ marginTop: '16px' }}>
          <CodeBlock>
{`import { useHistory, useLocation } from 'react-router-dom';

const Customers = ({ accountId }) => {
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;

  // Determine route prefix based on current path
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin ? '/admin-track' :
                      isEmployeeAdmin ? '/admin-employee' : '/admin';

  const handleSendCampaign = () => {
    // Use context-aware routing
    history.push(\`\${routePrefix}/customers/send-campaign\`, {
      segmentData,
      accountId,
      returnTo: location.pathname
    });
  };

  return (
    <QuickActions
      actions={[
        {
          label: 'Send Campaign',
          onClick: handleSendCampaign,
        },
      ]}
    />
  );
};`}
          </CodeBlock>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Route Definition Order</SectionTitle>
        <SectionDescription>
          Routes are defined in <code>/src/routes/index.js</code> using specific route
          wrappers. More specific routes should be defined before general routes.
        </SectionDescription>

        <CodeBlock>
{`import { Switch, Route } from 'react-router-dom';
import TrackAdminRoute from './TrackAdminRoute';
import EmployeeRoute from './EmployeeRoute';
import AdminRoute from './AdminRoute';

const Routes = () => {
  return (
    <Switch>
      {/* Track Admin Routes - Most specific first */}
      <TrackAdminRoute
        exact
        path="/admin-track/customers"
        component={Customers}
      />
      <TrackAdminRoute
        exact
        path="/admin-track/customers/:id"
        component={CustomerDetail}
      />

      {/* Employee Admin Routes */}
      <EmployeeRoute
        exact
        path="/admin-employee/customers"
        component={Customers}
      />
      <EmployeeRoute
        exact
        path="/admin-employee/customers/:id"
        component={CustomerDetail}
      />

      {/* Regular Admin Routes */}
      <AdminRoute
        exact
        path="/admin/customers"
        component={Customers}
      />
      <AdminRoute
        exact
        path="/admin/customers/:id"
        component={CustomerDetail}
      />
    </Switch>
  );
};`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Helper Hook Pattern</SectionTitle>
        <SectionDescription>
          You can create a reusable hook to avoid repeating the context detection logic.
        </SectionDescription>

        <CodeBlock>
{`// hooks/useRoutePrefix.js
import { useLocation } from 'react-router-dom';

/**
 * Hook to determine the current admin context route prefix
 * @returns {string} The route prefix (/admin-track, /admin-employee, or /admin)
 */
const useRoutePrefix = () => {
  const location = useLocation();
  const { pathname } = location;

  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');

  return isTrackAdmin ? '/admin-track' :
         isEmployeeAdmin ? '/admin-employee' : '/admin';
};

export default useRoutePrefix;

// Usage in components
import useRoutePrefix from 'hooks/useRoutePrefix';

const Component = () => {
  const routePrefix = useRoutePrefix();

  return (
    <Link to={\`\${routePrefix}/customers/\${id}\`}>
      View Customer
    </Link>
  );
};`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Navigation with State</SectionTitle>
        <SectionDescription>
          When passing state through navigation, always use the context-aware prefix.
        </SectionDescription>

        <CodeBlock>
{`import { useHistory, useLocation } from 'react-router-dom';
import useRoutePrefix from 'hooks/useRoutePrefix';

const CustomerList = () => {
  const history = useHistory();
  const location = useLocation();
  const routePrefix = useRoutePrefix();

  const handleSendCampaign = (segmentData) => {
    // Navigate with state
    history.push(\`\${routePrefix}/customers/send-campaign\`, {
      segmentData,
      returnTo: location.pathname, // Save current path for back navigation
    });
  };

  const handleViewCustomer = (customerId) => {
    // Simple navigation
    history.push(\`\${routePrefix}/customers/\${customerId}\`);
  };

  return (
    <div>
      <button onClick={() => handleSendCampaign(data)}>
        Send Campaign
      </button>
      <button onClick={() => handleViewCustomer(123)}>
        View Customer
      </button>
    </div>
  );
};`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Common Mistakes to Avoid</SectionTitle>
        <ComponentDemo direction="column" gap="16px">
          <Card title="❌ Wrong: Hardcoded Routes" accentTitle>
            <CodeBlock>
{`// DON'T DO THIS - Breaks in other contexts
const Component = () => {
  return (
    <Link to="/admin/customers/123">
      View Customer
    </Link>
  );
};

// This will break when used in Track Admin or Employee Admin!`}
            </CodeBlock>
          </Card>

          <Card title="✅ Correct: Context-Aware Routes" accentTitle>
            <CodeBlock>
{`// DO THIS - Works in all contexts
import { useLocation } from 'react-router-dom';

const Component = () => {
  const location = useLocation();
  const { pathname } = location;

  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin ? '/admin-track' :
                      isEmployeeAdmin ? '/admin-employee' : '/admin';

  return (
    <Link to={\`\${routePrefix}/customers/123\`}>
      View Customer
    </Link>
  );
};`}
            </CodeBlock>
          </Card>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <ComponentDemo direction="column" gap="16px">
          <Card title="✅ Do" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Always use context-aware routing in shared components
              <br />
              • Create a reusable hook (useRoutePrefix) for consistency
              <br />
              • Test components in all three admin contexts
              <br />
              • Use route state to maintain navigation context
              <br />
              • Document when components require context-aware routing
              <br />• Pass returnTo in state for proper back navigation
            </div>
          </Card>

          <Card title="❌ Don't" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Don't hardcode route prefixes in shared components
              <br />
              • Don't assume which admin context you're in
              <br />
              • Don't forget to test navigation across contexts
              <br />
              • Don't mix relative and absolute paths inconsistently
              <br />• Don't ignore the context when creating new links
            </div>
          </Card>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Testing Context-Aware Routing</SectionTitle>
        <SectionDescription>
          Always test your components in all three admin contexts to ensure navigation
          works correctly.
        </SectionDescription>

        <Card>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <strong>Manual Testing Checklist:</strong>
            <br />
            <br />
            1. Test navigation in <strong>Track Admin</strong> (/admin-track/...)
            <br />
            2. Test navigation in <strong>Employee Admin</strong> (/admin-employee/...)
            <br />
            3. Test navigation in <strong>Regular Admin</strong> (/admin/...)
            <br />
            4. Verify all links use the correct prefix
            <br />
            5. Check that back navigation returns to the correct context
            <br />
            6. Confirm breadcrumbs show the correct path
            <br />
            <br />
            <strong>What to Look For:</strong>
            <br />• Links should navigate within the same context
            <br />• URL should maintain the correct prefix
            <br />• State should persist across navigation
            <br />• No 404 errors from wrong prefixes
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Components Requiring Context-Aware Routing</SectionTitle>
        <Card>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            These components are shared across contexts and require context-aware
            routing:
            <br />
            <br />
            • <strong>CustomerTable</strong> - Links to customer detail pages
            <br />
            • <strong>UserLists</strong> - Links to user profiles
            <br />
            • <strong>QuickActions</strong> - Navigation to campaign and export pages
            <br />
            • <strong>Breadcrumbs</strong> - Navigation back through page hierarchy
            <br />
            • <strong>SearchResults</strong> - Links to search result details
            <br />
            • <strong>Navigation Menus</strong> - Top-level navigation
            <br />
            <br />
            <strong>Tip:</strong> If a component creates any <code>&lt;Link&gt;</code> or
            uses <code>history.push()</code>, it likely needs context-aware routing.
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Project Documentation</SectionTitle>
        <SectionDescription>
          This pattern is documented in the project's{' '}
          <code>/Users/bobby/Work/pitpay-admin/CLAUDE.md</code> file under "CRITICAL:
          Context-Aware Routing".
        </SectionDescription>
      </Section>
    </div>
  );
};

export default RoutingDocs;